import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { clientConfig } from './api/axios/config';
import { useSyncExternalStore } from 'react';

export class SignalRService {
  private connection: HubConnection | null = null;
  private isConnecting = false;
  private listeningTo: Record<string, ((...args: unknown[]) => void)[]> = {};
  private listeners: Set<() => void> = new Set();

  public async ensureConnection(hubUrl: string, token: string) {
    if (this.connection?.state === 'Connected') {
      return;
    }

    if (this.isConnecting) {
      while (this.isConnecting) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return;
    }
    await this.startConnection(hubUrl, token);
  }

  private async startConnection(hubUrl: string, token?: string) {
    try {
      this.isConnecting = true;
      const platform = clientConfig.getPlatform();
      const accessTokenFactory = platform === 'Auth0' ? `Bearer-Auth0 ${token}` : `Bearer ${token}`;

      this.connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
          accessTokenFactory: () => accessTokenFactory,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      this.connection.onclose(async () => {
        console.log('Connection closed, attempting to restart...');
        this.connection = null;
        // await this.startConnection(hubUrl, token);
      });

      this.connection.onreconnected(async () => {
        console.log('Connection reconnected, rejoining groups...');
        await this.rejoin();
      });

      await this.connection.start();

      console.log('SignalR Connected!');
      await this.rejoin();
    } catch (err) {
      console.error('SignalR Connection Error: ', err);

      setTimeout(() => this.startConnection(hubUrl), 30000);
      throw err;
    } finally {
      this.isConnecting = false;
    }
  }

  public addListener(methodName: string, callback: (...args: unknown[]) => void, reconnecting = false) {
    if (!this.listeningTo[methodName]) {
      this.listeningTo[methodName] = [];
    }

    if (this.listeningTo[methodName].indexOf(callback) !== -1 && !reconnecting) {
      return;
    }

    if (!reconnecting) {
      this.listeningTo[methodName].push(callback);
    }

    this.connection?.on(methodName, callback);
  }

  public removeListener(methodName: string, callback: (...args: unknown[]) => void) {
    const index = this.listeningTo[methodName]?.indexOf(callback);
    if (index >= 0) {
      this.listeningTo[methodName].splice(index, 1);
    }
    this.connection?.off(methodName, callback);
  }

  public async invoke(methodName: string, ...args: unknown[]) {
    try {
      await this.connection?.invoke(methodName, ...args);
    } catch (err) {
      console.error('SignalR Invoke Error: ', err);
      throw err;
    }
  }

  public async join(groupId: string) {
    if (!this.connection) {
      console.warn('No connection available for joining group');
      return;
    }

    try {
      await this.connection.invoke('AddToGroup', groupId);
      console.log('Joined group:', groupId);
    } catch (error) {
      console.error('Error joining group:', groupId, error);

      setTimeout(() => this.join(groupId), 5000);
    }
  }

  public async leave(groupId: string) {
    if (!this.connection) {
      return;
    }

    try {
      await this.connection.invoke('RemoveFromGroup', groupId);
      console.log('Left group:', groupId);
      this.listeningTo = {};
    } catch (error) {
      console.error('Error leaving group:', groupId, error);

      setTimeout(() => this.leave(groupId), 5000);
    }
  }

  private async rejoin() {
    const clientId = clientConfig.getClient();
    await this.join(clientId);
    await this.join(`${clientId}-venue-all`); // this is needed to receive notifications, needs a tasks to implement this correctly based on auth changes in the old admin applications

    Object.entries(this.listeningTo).forEach(([methodName, callbacks]) => {
      callbacks.forEach((callback) => this.addListener(methodName, callback, true));
    });
  }

  public stop() {
    this.connection?.stop();
    this.connection = null;
    this.listeningTo = {};
  }

  public get isConnected() {
    return this.connection?.state === 'Connected';
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }
}

export const signalR = new SignalRService();

export const useSignalRState = () => {
  return useSyncExternalStore(
    (listener) => signalR.subscribe(listener),
    () => signalR.isConnected ?? false,
  );
};
